import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  useTheme,
  Alert,
  Snackbar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  BusinessCenter as OwnerIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  CalendarMonth as CalendarIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import StatCard from '../../components/data/StatCard';
import { profilesApi, propertiesApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { formatDate as formatGregorianDate } from '../../../lib/dateUtils';

interface Owner {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  country?: string;
  nationality?: string;
  role: string;
  status: string;
  is_foreign?: boolean;
  id_number?: string;
  created_at: string;
  updated_at: string;
}

interface Property {
  id: string;
  title: string;
  address: string;
  city: string;
  status: string;
  property_type: string;
  owner_id: string;
}

export default function OwnerDetailPage() {
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['owners', 'common', 'people']);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Property management state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [propertyToRemove, setPropertyToRemove] = useState<Property | null>(null);
  const [availableProperties, setAvailableProperties] = useState<Property[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [assigningProperties, setAssigningProperties] = useState(false);
  const [removingProperty, setRemovingProperty] = useState(false);

  // Delete owner state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingOwner, setDeletingOwner] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Fetch owner details from Supabase
  const {
    data: owner,
    loading: ownerLoading,
    error: ownerError,
    refetch: refetchOwner
  } = useApi(() => profilesApi.getById(id || ''), [id]);

  // Fetch all properties to filter by owner
  const {
    data: allProperties,
    loading: propertiesLoading,
    error: propertiesError,
    refetch: refetchProperties
  } = useApi(() => propertiesApi.getAll(), []);

  // Filter properties owned by this owner
  const properties = (allProperties || []).filter((p: Property) => p.owner_id === id);

  // Show error message if API call fails
  useEffect(() => {
    if (ownerError) {
      setErrorMessage(ownerError);
    } else if (propertiesError) {
      setErrorMessage(propertiesError);
    }
  }, [ownerError, propertiesError]);

  const getInitials = (firstName: string, lastName: string) => {
    const first = firstName?.trim()[0] || '';
    const last = lastName?.trim()[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  const getFullName = () => {
    if (!owner) return 'Loading...';
    return `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'Unknown';
  };

  const formatDate = (dateString: string) => {
    return formatGregorianDate(dateString) || 'N/A';
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'error';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getPropertyStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return 'success';
      case 'rented':
        return 'info';
      case 'sold_out':
        return 'error';
      case 'under_maintenance':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Calculate stats
  const stats = {
    totalProperties: properties.length,
    availableProperties: properties.filter((p: Property) => p.status === 'available').length,
    rentedProperties: properties.filter((p: Property) => p.status === 'rented').length,
    memberSince: owner?.created_at ? new Date(owner.created_at).getFullYear() : 0,
  };

  const handleRefresh = () => {
    refetchOwner();
    refetchProperties();
    setErrorMessage(null);
  };

  // Open assign modal and fetch available properties
  const handleOpenAssignModal = async () => {
    setAssignModalOpen(true);
    setLoadingAvailable(true);
    setSelectedPropertyIds([]);

    try {
      const response = await propertiesApi.getAvailableForAssignment();
      if (response.error) {
        setErrorMessage(response.error.message);
        setAvailableProperties([]);
      } else {
        setAvailableProperties(response.data || []);
      }
    } catch (err) {
      setErrorMessage('Failed to load available properties');
      setAvailableProperties([]);
    } finally {
      setLoadingAvailable(false);
    }
  };

  // Handle property selection toggle
  const handlePropertyToggle = (propertyId: string) => {
    setSelectedPropertyIds(prev =>
      prev.includes(propertyId)
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  // Assign selected properties to owner
  const handleAssignProperties = async () => {
    if (selectedPropertyIds.length === 0 || !id) return;

    setAssigningProperties(true);
    try {
      const response = await propertiesApi.assignToOwner(selectedPropertyIds, id);
      if (response.error) {
        setErrorMessage(response.error.message);
      } else {
        const { success, failed } = response.data || { success: 0, failed: 0 };
        if (failed > 0) {
          setErrorMessage(t('propertyAssignmentFailed', { count: failed }));
        }
        if (success > 0) {
          setSuccessMessage(t('propertiesAssigned', { count: success }));
        }
        setAssignModalOpen(false);
        refetchProperties();
      }
    } catch (err) {
      setErrorMessage(t('propertyAssignmentError', { message: 'Unknown error' }));
    } finally {
      setAssigningProperties(false);
    }
  };

  // Open remove confirmation dialog
  const handleOpenRemoveDialog = (property: Property) => {
    setPropertyToRemove(property);
    setRemoveDialogOpen(true);
  };

  // Remove property from owner
  const handleRemoveProperty = async () => {
    if (!propertyToRemove) return;

    setRemovingProperty(true);
    try {
      const response = await propertiesApi.removeFromOwner(propertyToRemove.id);
      if (response.error) {
        setErrorMessage(response.error.message);
      } else {
        setSuccessMessage(t('propertyRemoved'));
        setRemoveDialogOpen(false);
        setPropertyToRemove(null);
        refetchProperties();
      }
    } catch (err) {
      setErrorMessage(t('propertyRemovedError'));
    } finally {
      setRemovingProperty(false);
    }
  };

  // Delete owner
  const handleDeleteOwner = async () => {
    if (!id) return;

    setDeletingOwner(true);
    setDeleteError(null);

    try {
      const response = await profilesApi.deleteOwner(id);

      if (response.error) {
        // Check if error has a translation key in details
        if (response.error.details === 'deleteWarningProperties') {
          setDeleteError(t('deleteWarningProperties'));
        } else if (response.error.details === 'ownerNotFoundOrAccessDenied') {
          setDeleteError(t('ownerNotFoundOrAccessDenied'));
        } else {
          setDeleteError(response.error.message);
        }
        return;
      }

      // Success - navigate back to owners list
      setSuccessMessage(t('ownerDeleted'));
      navigate('/dashboard/owners');
    } catch (err) {
      console.error('Error deleting owner:', err);
      setDeleteError(err instanceof Error ? err.message : t('common:somethingWentWrong'));
    } finally {
      setDeletingOwner(false);
    }
  };

  // Get display phone (prefer phone_number over phone)
  const displayPhone = owner?.phone_number || owner?.phone;

  if (ownerLoading) {
    return (
      <ResponsiveContainer>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width="60%" height={40} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={120} />
            </Grid>
          ))}
        </Grid>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Skeleton variant="text" width="40%" height={32} />
            <Skeleton variant="rectangular" height={200} />
          </CardContent>
        </Card>
      </ResponsiveContainer>
    );
  }

  if (ownerError && !owner) {
    return (
      <ResponsiveContainer>
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {t('ownerNotFound')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('ownerNotFoundDescription')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/owners')}
          >
            {t('backToOwners')}
          </Button>
        </Box>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setErrorMessage(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem' },
            }}
          >
            {getFullName()}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t('ownerDetails')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={ownerLoading || propertiesLoading}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            {t('common:refresh')}
          </Button>

          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard/owners')}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            {t('common:back')}
          </Button>

          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/dashboard/owners/${id}/edit`)}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            {t('editOwner')}
          </Button>

          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
            sx={{ minWidth: { xs: 'auto', sm: 'auto' } }}
          >
            {t('deleteOwner')}
          </Button>
        </Box>
      </Box>

      {/* Profile Header */}
      <Card sx={{ mb: 3, background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}05)` }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
                fontWeight: 'bold',
              }}
            >
              {getInitials(owner?.first_name || '', owner?.last_name || '')}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {getFullName()}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {owner?.email || t('notProvided')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  label={t(`statusOptions.${owner?.status}`) || owner?.status}
                  color={getStatusColor(owner?.status || '') as any}
                  size="small"
                />
                {owner?.is_foreign && (
                  <Chip
                    label={t('isForeign')}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
                {owner?.nationality && (
                  <Chip
                    label={owner.nationality}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('propertiesOwned')}
            value={stats.totalProperties}
            icon={<HomeIcon />}
            color="primary"
            loading={propertiesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('available')}
            value={stats.availableProperties}
            icon={<HomeIcon />}
            color="success"
            loading={propertiesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('rented')}
            value={stats.rentedProperties}
            icon={<HomeIcon />}
            color="info"
            loading={propertiesLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('memberSince')}
            value={stats.memberSince}
            icon={<CalendarIcon />}
            color="warning"
            loading={ownerLoading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Contact Information */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t('contactInformation')}
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <EmailIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('emailAddress')}
                    secondary={owner?.email || t('notProvided')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PhoneIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('phoneNumber')}
                    secondary={displayPhone || t('notProvided')}
                  />
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <LocationIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('address')}
                    secondary={owner?.address || t('notProvided')}
                  />
                </ListItem>

                {owner?.city && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('city')}
                      secondary={owner.city}
                    />
                  </ListItem>
                )}

                {owner?.country && (
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('country')}
                      secondary={owner.country}
                    />
                  </ListItem>
                )}

                {owner?.nationality && (
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('nationality')}
                      secondary={owner.nationality}
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Properties Owned */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {t('ownedProperties')}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAssignModal}
                >
                  {t('assignProperty')}
                </Button>
              </Box>

              {properties.length > 0 ? (
                <List>
                  {properties.map((property: Property, index: number) => (
                    <Box key={property.id}>
                      <ListItem
                        sx={{ cursor: 'pointer', pr: 8 }}
                        onClick={() => navigate(`/dashboard/properties/${property.id}`)}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label="remove"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRemoveDialog(property);
                            }}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <HomeIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={property.title}
                          secondary={`${property.address || ''}, ${property.city || ''}`}
                        />
                        <Chip
                          label={t(property.status) || property.status}
                          color={getPropertyStatusColor(property.status) as any}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                      </ListItem>
                      {index < properties.length - 1 && <Divider />}
                    </Box>
                  ))}
                </List>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <HomeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('noProperties')}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Account Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                {t('accountDetails')}
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('ownerID')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                    {owner?.id || t('notAvailable')}
                  </Typography>
                </Grid>

                {owner?.id_number && (
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('idNumber')}
                    </Typography>
                    <Typography variant="body1">
                      {owner.id_number}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('role')}
                  </Typography>
                  <Typography variant="body1">
                    {owner?.role || t('notAvailable')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('status')}
                  </Typography>
                  <Chip
                    label={t(`statusOptions.${owner?.status}`) || owner?.status}
                    color={getStatusColor(owner?.status || '') as any}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('memberSince')}
                  </Typography>
                  <Typography variant="body1">
                    {owner?.created_at ? formatDate(owner.created_at) : t('notAvailable')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Assign Property Modal */}
      <Dialog
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('assignProperty')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('selectPropertiesToAssign')}
          </Typography>

          {loadingAvailable ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableProperties.length > 0 ? (
            <List>
              {availableProperties.map((property) => (
                <ListItem
                  key={property.id}
                  onClick={() => handlePropertyToggle(property.id)}
                  sx={{ borderRadius: 1, mb: 0.5, cursor: 'pointer' }}
                >
                  <Checkbox
                    edge="start"
                    checked={selectedPropertyIds.includes(property.id)}
                    tabIndex={-1}
                    disableRipple
                  />
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <HomeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={property.title}
                    secondary={`${property.address || ''}, ${property.city || ''}`}
                  />
                  <Chip
                    label={t(property.status) || property.status}
                    color={getPropertyStatusColor(property.status) as any}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <HomeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                {t('noPropertiesAvailable')}
              </Typography>
            </Box>
          )}

          {selectedPropertyIds.length > 0 && (
            <Typography variant="body2" color="primary" sx={{ mt: 2 }}>
              {t('selectedProperties', { count: selectedPropertyIds.length })}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignModalOpen(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignProperties}
            disabled={selectedPropertyIds.length === 0 || assigningProperties}
            startIcon={assigningProperties ? <CircularProgress size={16} /> : null}
          >
            {t('assign')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Property Confirmation Dialog */}
      <Dialog
        open={removeDialogOpen}
        onClose={() => setRemoveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('confirmRemovePropertyTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('confirmRemoveProperty')}
          </Typography>
          {propertyToRemove && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {propertyToRemove.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {propertyToRemove.address}, {propertyToRemove.city}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveDialogOpen(false)}>
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveProperty}
            disabled={removingProperty}
            startIcon={removingProperty ? <CircularProgress size={16} /> : null}
          >
            {t('remove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Owner Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deletingOwner && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('deleteOwner')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('confirmDeleteOwner')}
          </Typography>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            disabled={deletingOwner}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteOwner}
            disabled={deletingOwner}
            startIcon={deletingOwner ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deletingOwner ? t('common:deleting') : t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </ResponsiveContainer>
  );
}
